import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useRoleDefinitions, useRolePermissions } from '@/hooks/usePermissions';
import { usePermissions } from '@/hooks/usePermissions';
import { DOMAIN_LABELS, DOMAIN_ACTIONS, type RoleDefinition } from '@/lib/permissions';
import { ChevronRight, Shield, Plus, Copy, Trash2 } from 'lucide-react';
import { CreateRoleDialog } from './CreateRoleDialog';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

type RolePermissionRow = { domain: string; action: string; scope: string };
type CloneRoleSource = {
  name: string;
  description: string | null;
  level: number;
  permissions: RolePermissionRow[];
};

export function RoleDetails() {
  const { t } = useTranslation();
  const { data: roles, isLoading } = useRoleDefinitions();
  const { can } = usePermissions();
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [cloneSource, setCloneSource] = useState<CloneRoleSource | null>(null);

  const selected = roles?.find(r => r.id === selectedRole);
  const canManageRoles = can('roles_permissions', 'create');

  if (isLoading) {
    return <Card className="p-6 rounded-xl"><Skeleton className="h-64 w-full rounded-xl" /></Card>;
  }

  const systemRoles = roles?.filter(r => r.is_system) || [];
  const customRoles = roles?.filter(r => !r.is_system) || [];

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {/* Role list */}
        <Card className="rounded-xl shadow-card border-border p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-semibold text-foreground flex items-center gap-2">
              <Shield className="w-4 h-4 text-accent" /> {t('hire.roles.roles')}
            </h3>
            {canManageRoles && (
              <Button size="sm" variant="outline" className="gap-1.5 h-8 text-xs" onClick={() => { setCloneSource(null); setCreateOpen(true); }}>
                <Plus className="w-3.5 h-3.5" /> {t('hire.roles.new')}
              </Button>
            )}
          </div>

          <div className="space-y-1.5">
            {systemRoles.length > 0 && (
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider px-3 pt-2">{t('hire.roles.system')}</p>
            )}
            {systemRoles.map((r) => (
              <RoleButton key={r.id} role={r} selected={selectedRole === r.id} onClick={() => setSelectedRole(r.id)} />
            ))}

            {customRoles.length > 0 && (
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider px-3 pt-3">{t('hire.roles.custom')}</p>
            )}
            {customRoles.map((r) => (
              <RoleButton key={r.id} role={r} selected={selectedRole === r.id} onClick={() => setSelectedRole(r.id)} />
            ))}
          </div>
        </Card>

        {/* Permission detail */}
        <Card className="md:col-span-2 rounded-xl shadow-card border-border p-5 overflow-x-hidden overflow-y-auto overscroll-contain flex flex-col max-h-[calc(100dvh-12rem)]">
          {!selected ? (
            <div className="text-center text-muted-foreground py-20">
              <Shield className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">{t('hire.roles.selectRole')}</p>
            </div>
          ) : (
            <RolePermissionDetail
              role={selected}
              canManage={canManageRoles}
              onClone={(perms) => {
                setCloneSource({
                  name: selected.name,
                  description: selected.description,
                  level: selected.level,
                  permissions: perms,
                });
                setCreateOpen(true);
              }}
              onDeleted={() => setSelectedRole(null)}
            />
          )}
        </Card>
      </div>

      <CreateRoleDialog
        key={cloneSource ? 'clone' : 'create'}
        open={createOpen}
        onOpenChange={setCreateOpen}
        cloneFrom={cloneSource}
      />
    </>
  );
}

function RoleButton({ role, selected, onClick }: { role: RoleDefinition; selected: boolean; onClick: () => void }) {
  const { t } = useTranslation();

  return (
    <button
      onClick={onClick}
      className={`w-full text-start p-3 rounded-xl transition-colors flex items-center justify-between group ${
        selected
          ? 'bg-accent/10 border border-accent/20'
          : 'hover:bg-muted/50 border border-transparent'
      }`}
    >
      <div>
        <p className="text-sm font-medium text-foreground">{role.name}</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          {t("hire.roles.level", { level: role.level })}
          {!role.is_system && <span className="ms-1.5 text-accent">- {t("hire.roles.custom")}</span>}
        </p>
      </div>
      <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${selected ? 'rotate-90' : ''}`} />
    </button>
  );
}

function RolePermissionDetail({
  role,
  canManage,
  onClone,
  onDeleted,
}: {
  role: RoleDefinition;
  canManage: boolean;
  onClone: (perms: RolePermissionRow[]) => void;
  onDeleted: () => void;
}) {
  const { t } = useTranslation();
  const { data: perms, isLoading } = useRolePermissions(role.id);
  const queryClient = useQueryClient();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const permSet = new Set(perms?.map(p => `${p.domain}:${p.action}`) || []);
  const domainLabel = (domain: string) => t(`hire.permissions.domains.${domain}`);
  const actionLabel = (action: string) => t(`hire.permissions.actions.${action}`);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      // Delete permissions first, then role
      await supabase.from('role_permissions').delete().eq('role_definition_id', role.id);
      const { error } = await supabase.from('role_definitions').delete().eq('id', role.id);
      if (error) throw error;
      toast.success(t("hire.roles.deletedToast"));
      queryClient.invalidateQueries({ queryKey: ['role-definitions'] });
      onDeleted();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t("hire.roles.deleteFailed");
      toast.error(message);
    } finally {
      setDeleting(false);
      setDeleteOpen(false);
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="mb-5 flex items-start justify-between">
        <div>
          <h3 className="font-display font-semibold text-foreground text-lg">{role.name}</h3>
          {role.description && <p className="text-sm text-muted-foreground mt-1">{role.description}</p>}
          <div className="flex gap-2 mt-2">
            <Badge variant="outline" className="text-xs">{t("hire.roles.level", { level: role.level })}</Badge>
            <Badge variant="outline" className="text-xs capitalize">{t("hire.roles.baseRole", { role: role.base_role.replace("_", " ") })}</Badge>
            {role.is_system && <Badge variant="secondary" className="text-xs">{t("hire.roles.system")}</Badge>}
          </div>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5 h-8 text-xs"
              onClick={() => onClone(perms || [])}
            >
              <Copy className="w-3.5 h-3.5" /> {t("hire.roles.clone")}
            </Button>
            {!role.is_system && (
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5 h-8 text-xs text-destructive hover:text-destructive"
                onClick={() => setDeleteOpen(true)}
              >
                <Trash2 className="w-3.5 h-3.5" /> {t("common.delete")}
              </Button>
            )}
          </div>
        )}
      </div>

      {isLoading ? (
        <Skeleton className="h-40 w-full rounded-xl" />
      ) : (
        <ScrollArea type="always" className="flex-1 min-h-0 pe-2">
          <div className="space-y-4">
            {Object.keys(DOMAIN_LABELS).map((domain) => {
              const actions = DOMAIN_ACTIONS[domain] || [];
              const grantedActions = actions.filter(a => permSet.has(`${domain}:${a}`));

              return (
                <div key={domain} className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground">{domainLabel(domain)}</span>
                    <span className="text-xs text-muted-foreground">
                      ({grantedActions.length}/{actions.length})
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {actions.map(action => {
                      const granted = permSet.has(`${domain}:${action}`);
                      return (
                        <Badge
                          key={action}
                          variant="outline"
                          className={`text-xs ${
                            granted
                              ? 'bg-accent/10 text-accent border-accent/20'
                              : 'bg-muted/30 text-muted-foreground border-border opacity-50'
                          }`}
                        >
                          {actionLabel(action)}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>
      )}

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("hire.roles.deleteTitle", { name: role.name })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("hire.roles.deleteDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {deleting ? t("hire.roles.deleting") : t("hire.roles.deleteRole")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
